import React, { useEffect, useMemo, useRef, useState } from 'react';
import useCoinGecko from '../hooks/useCoinGecko';
import {
  CoinGeckoGlobalData,
  CoinGeckoMarket,
  CoinGeckoResourceState,
  CoinGeckoSearchResult,
} from '../types';
import { buildSearchResourceKey } from '../utils/coingecko';
import { COINGECKO_PAGE_TABS, DEFAULT_COINGECKO_TAB } from '../utils/coingeckoRegistry';

function formatCompactCurrency(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
}

function formatPrice(value: number): string {
  if (value < 0.0001) return `$${value.toFixed(8)}`;
  if (value < 0.01) return `$${value.toFixed(6)}`;
  if (value < 1) return `$${value.toFixed(4)}`;
  return `$${value.toLocaleString()}`;
}

function renderSparkline(prices: number[], color: string) {
  if (!prices || prices.length < 2) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const width = 80;
  const height = 32;
  const step = width / (prices.length - 1);
  const path = prices
    .map((price, index) => {
      const x = index * step;
      const y = height - ((price - min) / range) * height;
      return `${index === 0 ? 'M' : 'L'} ${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none">
      <path d={path} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CoinRow: React.FC<{ market: CoinGeckoMarket }> = ({ market }) => {
  const positive = market.priceChangePercentage24h >= 0;
  const color = positive ? '#10B981' : '#EF4444';

  return (
    <div className="grid grid-cols-12 items-center gap-3 rounded-2xl border border-white/30 bg-white/60 px-4 py-3">
      <div className="col-span-5 flex items-center gap-3">
        <img src={market.image} alt={market.name} className="h-9 w-9 rounded-full" />
        <div>
          <div className="text-sm font-bold text-gray-800">{market.name}</div>
          <div className="text-xs uppercase text-gray-400">{market.symbol}</div>
        </div>
      </div>
      <div className="col-span-2 text-right text-sm font-bold text-gray-800">{formatPrice(market.currentPrice)}</div>
      <div className={`col-span-2 text-right text-sm font-bold ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
        {positive ? '+' : ''}{market.priceChangePercentage24h.toFixed(2)}%
      </div>
      <div className="col-span-1 hidden text-right text-xs text-gray-500 lg:block">{formatCompactCurrency(market.marketCap)}</div>
      <div className="col-span-3 flex justify-end lg:col-span-2">{renderSparkline(market.sparklineIn7d, color)}</div>
    </div>
  );
};

const CoinGeckoPage: React.FC = () => {
  const {
    trackedMarketIds,
    resources,
    ensureResource,
    searchCoins,
    getMarketById,
  } = useCoinGecko();
  const [activeTab, setActiveTab] = useState(DEFAULT_COINGECKO_TAB);
  const [query, setQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (activeTab === 'trending' || activeTab === 'global') {
      void ensureResource(activeTab);
    }
  }, [activeTab, ensureResource]);

  useEffect(() => {
    if (activeTab !== 'search') {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (!query.trim()) {
      return;
    }
    debounceRef.current = setTimeout(() => {
      void searchCoins(query);
    }, 400);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [activeTab, query, searchCoins]);

  const trackedMarkets = useMemo(() => {
    return trackedMarketIds
      .map((id) => getMarketById(id))
      .filter(Boolean)
      .sort((left, right) => (right?.marketCap ?? 0) - (left?.marketCap ?? 0)) as CoinGeckoMarket[];
  }, [getMarketById, trackedMarketIds]);

  const trendingIds = ((resources.trending as CoinGeckoResourceState<string[]> | undefined)?.data ?? []);
  const trendingMarkets = trendingIds
    .map((id) => getMarketById(id))
    .filter(Boolean) as CoinGeckoMarket[];
  const globalData = (resources.global as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined)?.data ?? null;
  const searchState = resources[buildSearchResourceKey(query)] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined;
  const searchResults = searchState?.data ?? [];
  const activeConfig = COINGECKO_PAGE_TABS.find((tab) => tab.id === activeTab) ?? COINGECKO_PAGE_TABS[0];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 md:px-8">
      <div className="rounded-3xl border border-white/30 bg-white/60 p-6 shadow-lg backdrop-blur">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-fuchsia-500">CoinGecko Hub</p>
            <h2 className="text-3xl font-black text-gray-800">Centralized Market Data</h2>
            <p className="mt-2 max-w-3xl text-sm text-gray-500">
              Shared CoinGecko resources now feed the dashboard, watchlist, and market surfaces from one provider.
            </p>
          </div>
          <div className="rounded-2xl border border-fuchsia-100 bg-fuchsia-50 px-4 py-3 text-xs text-fuchsia-700">
            Active tab: <span className="font-bold">{activeConfig.label}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {COINGECKO_PAGE_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors ${
              activeTab === tab.id
                ? 'bg-fuchsia-500 text-white shadow-md'
                : 'bg-white/60 text-gray-600 hover:bg-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-3xl border border-white/30 bg-white/50 p-6 shadow-lg backdrop-blur">
        <div className="mb-4 flex flex-col gap-1">
          <h3 className="text-xl font-bold text-gray-800">{activeConfig.label}</h3>
          <p className="text-sm text-gray-500">{activeConfig.description}</p>
        </div>

        {activeTab === 'markets' && (
          <div className="space-y-3">
            {trackedMarkets.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">
                Market cache is warming up.
              </div>
            ) : (
              trackedMarkets.map((market) => <CoinRow key={market.id} market={market} />)
            )}
          </div>
        )}

        {activeTab === 'trending' && (
          <div className="space-y-3">
            {trendingMarkets.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">
                Trending data will appear here after the first lazy fetch finishes.
              </div>
            ) : (
              trendingMarkets.map((market) => <CoinRow key={market.id} market={market} />)
            )}
          </div>
        )}

        {activeTab === 'global' && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {globalData ? (
              <>
                <div className="rounded-2xl border border-white/30 bg-white/70 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400">Total Market Cap</div>
                  <div className="mt-2 text-2xl font-black text-gray-800">{formatCompactCurrency(globalData.total_market_cap.usd)}</div>
                </div>
                <div className="rounded-2xl border border-white/30 bg-white/70 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400">24h Volume</div>
                  <div className="mt-2 text-2xl font-black text-gray-800">{formatCompactCurrency(globalData.total_volume.usd)}</div>
                </div>
                <div className="rounded-2xl border border-white/30 bg-white/70 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400">BTC Dominance</div>
                  <div className="mt-2 text-2xl font-black text-amber-600">
                    {(globalData.market_cap_percentage.btc ?? 0).toFixed(1)}%
                  </div>
                </div>
                <div className="rounded-2xl border border-white/30 bg-white/70 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400">Active Cryptos</div>
                  <div className="mt-2 text-2xl font-black text-gray-800">{globalData.active_cryptocurrencies.toLocaleString()}</div>
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500 md:col-span-2 xl:col-span-4">
                Global market data will appear here after the lazy fetch finishes.
              </div>
            )}
          </div>
        )}

        {activeTab === 'search' && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/30 bg-white/70 p-4">
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search coins by name or symbol"
                className="w-full rounded-xl border border-fuchsia-200 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-fuchsia-400"
              />
            </div>
            {query.trim() && searchResults.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">
                {searchState?.status === 'loading'
                  ? 'Searching CoinGecko...'
                  : 'No cached search results yet for this query.'}
              </div>
            ) : null}
            {searchResults.map((result) => (
              <div key={result.id} className="flex items-center gap-3 rounded-2xl border border-white/30 bg-white/70 px-4 py-3">
                <img src={result.thumb} alt={result.name} className="h-9 w-9 rounded-full" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-gray-800">{result.name}</div>
                  <div className="text-xs uppercase text-gray-400">{result.symbol}</div>
                </div>
                <div className="text-xs text-gray-400">{result.id}</div>
              </div>
            ))}
          </div>
        )}

        {activeConfig.status === 'scaffold' && (
          <div className="rounded-2xl border border-dashed border-fuchsia-200 bg-fuchsia-50 p-6 text-sm text-fuchsia-700">
            Data layer prepared. This tab is registered but its fetch implementation is intentionally left for the next phase.
          </div>
        )}
      </div>
    </div>
  );
};

export default CoinGeckoPage;
